import { useState, useEffect } from "react";
import { useErs } from "src/context/ers/ers.provider";
import { useEgdHub } from "src/hooks/useEgdHub";
import { isPayloadEgdSource } from "src/utils/egdSource";

/**
 * Unified EGD hub loader — ERS or Payload based on REACT_APP_PROMOTIONS_SOURCE.
 *
 * @param {'en' | 'fr'} locale
 * @param {{ enabled?: boolean }} [options]
 * @returns {{
 *   gameList: import('src/utils/mapEgdGame').EgdGameCard[] | undefined,
 *   jackpotList: import('src/utils/mapEgdJackpot').EgdJackpotMeter[] | undefined,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export function useEgdHubData(locale, options = {}) {
  const { enabled = true } = options;
  const usePayload = isPayloadEgdSource() && enabled;
  const { data: ersData, fetchData, error: ersError } = useErs();
  const payload = useEgdHub(locale, { enabled: usePayload });

  const [ersLoading, setErsLoading] = useState(enabled && !isPayloadEgdSource());

  useEffect(() => {
    if (!enabled || isPayloadEgdSource()) return;

    const hasGameList = Object.prototype.hasOwnProperty.call(
      ersData,
      "egd-game-list"
    );
    const hasJackpots = Object.prototype.hasOwnProperty.call(
      ersData,
      "egd-progressive-jackpots"
    );

    if (hasGameList && hasJackpots) {
      setErsLoading(false);
      return;
    }

    setErsLoading(true);

    Promise.all([
      fetchData("egd-game-list"),
      fetchData("egd-progressive-jackpots"),
    ])
      .catch((err) => {
        console.error("Error fetching EGD data from ERS:", err);
      })
      .finally(() => {
        setErsLoading(false);
      });
  }, [enabled, fetchData, ersData]);

  if (usePayload) {
    return {
      gameList: payload.gameList,
      jackpotList: payload.jackpotList,
      loading: payload.loading,
      error: payload.error,
    };
  }

  return {
    gameList: ersData["egd-game-list"],
    jackpotList: ersData["egd-progressive-jackpots"],
    loading: ersLoading,
    error: ersError,
  };
}
